import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { 
  LayoutDashboard, Building2, Users, ChevronRight,
  UserPlus, Shield, GitBranch,
  Briefcase, Clock, CalendarDays, Calendar, DollarSign,
  Calculator, Target, Star, Award,
  GraduationCap, BookOpen, Heart,
  ClipboardList,
  BarChart3, Settings, ArrowLeft, Menu, X,
  FileText, Sparkles, UserCircle2,
  Gavel, AlertTriangle, FolderOpen, Scale,
  FileSpreadsheet, Database, ArrowLeftRight, Mail
} from 'lucide-react';
import { usePermissions } from '../context/PermissionsContext';

const NavItem = ({ item, nested, isActive, onMobileClose, sidebarOpen, mobileMenuOpen }) => {
  const active = isActive(item.path);
  const Icon = item.icon;
  
  return (
    <Link
      to={item.path}
      onClick={onMobileClose}
      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
      className={`flex items-center gap-3 px-3 py-2 text-[13px] transition-all ${
        nested ? 'pl-11' : ''
      } ${
        active 
          ? 'bg-emerald-500/15 text-emerald-400 font-medium border-r-2 border-emerald-400' 
          : 'text-slate-400 hover:bg-white/5 hover:text-slate-200'
      }`}
    >
      {Icon && <Icon className="w-4 h-4 flex-shrink-0" />}
      <span className={`truncate ${!sidebarOpen && !mobileMenuOpen ? 'hidden' : ''}`}>{item.label}</span>
    </Link>
  );
};

const NavSection = ({ 
  section, 
  isActive, 
  expandedSections, 
  toggleSection, 
  onMobileClose, 
  sidebarOpen, 
  mobileMenuOpen,
  pathname 
}) => {
  const isExpanded = expandedSections.includes(section.id);
  const sectionActive = section.items?.some(item => pathname === item.path);
  const Icon = section.icon;

  if (section.single) {
    const active = isActive(section.path);
    return (
      <Link
        to={section.path}
        onClick={onMobileClose}
        data-testid={`nav-${section.label.toLowerCase().replace(/\s+/g, '-')}`}
        className={`flex items-center gap-3 px-3 py-2.5 text-[13px] transition-all ${
          active 
            ? 'bg-emerald-500/15 text-emerald-400 font-medium border-r-2 border-emerald-400' 
            : 'text-slate-400 hover:bg-white/5 hover:text-slate-200'
        }`}
      >
        <Icon className="w-5 h-5 flex-shrink-0" />
        <span className={`truncate ${!sidebarOpen && !mobileMenuOpen ? 'hidden' : ''}`}>{section.label}</span>
      </Link>
    );
  }

  if (section.divider) {
    return (sidebarOpen || mobileMenuOpen) ? (
      <div className="mt-4 mb-1 px-3">
        <div className="text-[10px] font-semibold text-slate-500/70 uppercase tracking-[0.15em]">
          {section.label}
        </div>
      </div>
    ) : <div className="my-2 mx-3 border-t border-white/5" />;
  }

  return (
    <div>
      <button
        onClick={() => toggleSection(section.id)}
        data-testid={`nav-section-${section.id}`}
        className={`w-full flex items-center gap-3 px-3 py-2.5 text-[13px] transition-all ${
          sectionActive 
            ? 'bg-white/5 text-slate-200' 
            : 'text-slate-400 hover:bg-white/5 hover:text-slate-200'
        }`}
      >
        <Icon className="w-5 h-5 flex-shrink-0" />
        <span className={`flex-1 text-left truncate ${!sidebarOpen && !mobileMenuOpen ? 'hidden' : ''}`}>
          {section.label}
        </span>
        {(sidebarOpen || mobileMenuOpen) && (
          <ChevronRight className={`w-3.5 h-3.5 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`} />
        )}
      </button>
      
      {isExpanded && (sidebarOpen || mobileMenuOpen) && (
        <div className="mt-0.5 pb-1">
          {section.items?.map(item => (
            <NavItem 
              key={`${section.id}-${item.path}-${item.label}`} 
              item={item} 
              nested 
              isActive={isActive}
              onMobileClose={onMobileClose}
              sidebarOpen={sidebarOpen}
              mobileMenuOpen={mobileMenuOpen}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const HRLayout = ({ children }) => {
  const location = useLocation();
  const { isAdmin, can, isHRManager } = usePermissions();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [hrAuthReady, setHrAuthReady] = useState(false);
  const [hrAuthError, setHrAuthError] = useState(null);
  const lastExchangedToken = useRef(null);
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('hr_view_mode') || 'admin';
  });
  const [expandedSections, setExpandedSections] = useState(() => {
    const saved = localStorage.getItem('hr_expanded_sections');
    return saved ? JSON.parse(saved) : ['people'];
  });

  const isHRAdmin = isAdmin || can('hr_employees_manage') || isHRManager;

  useEffect(() => {
    const mainToken = localStorage.getItem('token');
    if (!mainToken) {
      setHrAuthReady(true);
      return;
    }

    if (lastExchangedToken.current === mainToken && localStorage.getItem('hr_core_token')) {
      setHrAuthReady(true);
      return;
    }

    setHrAuthReady(false);
    setHrAuthError(null);

    const doExchange = async () => {
      try {
        const resp = await axios.post('/api/auth/hr-exchange', {}, {
          headers: { Authorization: `Bearer ${mainToken}` }
        });
        if (resp.data?.token) {
          localStorage.setItem('hr_core_token', resp.data.token);
          localStorage.setItem('hr_core_user', JSON.stringify(resp.data.user));
          localStorage.setItem('hr_core_permissions', JSON.stringify(resp.data.permissions));
          lastExchangedToken.current = mainToken;
          setHrAuthError(null);
        } else {
          setHrAuthError('HR Core returned no token');
        }
      } catch (err) {
        console.warn('HR Core auto-auth failed:', err?.response?.data?.error || err.message);
        if (localStorage.getItem('hr_core_token')) {
          setHrAuthError(null);
        } else {
          setHrAuthError('Unable to connect to HR Core service. Leave and payroll features may be limited.');
        }
      } finally {
        setHrAuthReady(true);
      }
    };
    doExchange();
  }, []);

  useEffect(() => {
    const handleHrLogout = () => {
      setHrAuthReady(false);
      setHrAuthError(null);
      lastExchangedToken.current = null;
    };
    window.addEventListener('hr_core_logout', handleHrLogout);
    return () => window.removeEventListener('hr_core_logout', handleHrLogout);
  }, []);

  useEffect(() => {
    localStorage.setItem('hr_view_mode', viewMode);
  }, [viewMode]);

  useEffect(() => {
    localStorage.setItem('hr_expanded_sections', JSON.stringify(expandedSections));
  }, [expandedSections]);

  const toggleViewMode = useCallback(() => {
    setViewMode(prev => prev === 'admin' ? 'employee' : 'admin');
  }, []);

  const toggleSection = useCallback((section) => {
    setExpandedSections(prev => 
      prev.includes(section) 
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  }, []);

  const isActive = useCallback((path) => location.pathname === path, [location.pathname]);
  
  const handleMobileClose = useCallback(() => setMobileMenuOpen(false), []);

  const isHR = isAdmin || can('hr_view') || can('hr_employees_manage');
  const canManageAttendance = isAdmin || can('hr_view') || can('hr_attendance_manage');
  const canManageLeave = isAdmin || can('hr_view') || can('hr_leave_manage');
  const canManagePayroll = isAdmin || can('hr_view') || can('hr_payroll_manage') || can('hr_payroll_view');
  const canManagePerformance = isAdmin || can('hr_view') || can('hr_performance_manage');
  const isManager = isAdmin || can('hr_employees_manage');

  const navSections = useMemo(() => {
    const sections = [];
    const showAdminView = viewMode === 'admin' && isHRAdmin;

    if (!showAdminView) {
      sections.push({
        id: 'my-profile',
        label: 'My Profile',
        icon: UserCircle2,
        path: '/hr/employee-profile',
        single: true
      });
      sections.push({ id: 'div-my-stuff', label: 'My Workspace', divider: true });
      sections.push({
        id: 'my-time',
        label: 'Time & Attendance',
        icon: Clock,
        items: [
          { label: 'Time Tracking', path: '/hr/time-attendance', icon: Clock },
        ]
      });
      sections.push({
        id: 'my-leave',
        label: 'Leave',
        icon: Calendar,
        items: [
          { label: 'Leave Management', path: '/hr/leave-management', icon: Calendar },
        ]
      });
      sections.push({
        id: 'my-payslips',
        label: 'My Payslips',
        icon: DollarSign,
        path: '/hr/my-payslips',
        single: true
      });
      sections.push({
        id: 'my-performance',
        label: 'Goals & Reviews',
        icon: Target,
        path: '/hr/performance-management',
        single: true
      });
      sections.push({ id: 'div-my-more', label: 'More', divider: true });
      sections.push({
        id: 'my-training',
        label: 'Training',
        icon: GraduationCap,
        path: '/hr/learning',
        single: true
      });
      sections.push({
        id: 'my-docs',
        label: 'My Documents',
        icon: FolderOpen,
        path: '/employee/my-documents',
        single: true
      });
      sections.push({
        id: 'my-discipline',
        label: 'My Records',
        icon: Scale,
        path: '/hr/discipline-grievance',
        single: true
      });
      return sections;
    }

    sections.push({
      id: 'dashboard',
      label: 'Dashboard',
      icon: LayoutDashboard,
      path: '/hr',
      single: true
    });

    if (isHR) {
      sections.push({
        id: 'master-data',
        label: 'Master Data',
        icon: Database,
        path: '/hr/core-master-data',
        single: true
      });
    }

    sections.push({ id: 'div-workforce', label: 'Workforce', divider: true });

    const peopleItems = [];
    if (isHR || isManager) {
      peopleItems.push({ label: 'Employees', path: '/hr/employees', icon: Users });
      peopleItems.push({ label: 'Departments', path: '/hr/departments', icon: Building2 });
      peopleItems.push({ label: 'Org Chart', path: '/hr/org-chart', icon: GitBranch });
    }
    if (isHR) {
      peopleItems.push({ label: 'Onboarding', path: '/hr/onboarding', icon: UserPlus });
      peopleItems.push({ label: 'Invitations', path: '/hr/invitations', icon: Mail });
    }
    peopleItems.push({ label: 'My Profile', path: '/hr/employee-profile', icon: UserCircle2 });

    if (peopleItems.length > 0) {
      sections.push({
        id: 'people',
        label: 'People',
        icon: Users,
        items: peopleItems
      });
    }

    if (isHR) {
      sections.push({
        id: 'recruitment',
        label: 'Recruitment',
        icon: Briefcase,
        items: [
          { label: 'Job Postings', path: '/hr/jobs', icon: Briefcase },
          { label: 'Applicants', path: '/hr/recruitment', icon: UserPlus },
        ]
      });
    }

    sections.push({ id: 'div-operations', label: 'Operations', divider: true });

    const attendanceItems = [
      { label: 'Time Tracking', path: '/hr/time-attendance', icon: Clock },
    ];
    if (canManageAttendance || isManager) {
      attendanceItems.push({ label: 'Attendance Records', path: '/hr/attendance', icon: CalendarDays });
      attendanceItems.push({ label: 'Reports', path: '/hr/attendance-reports', icon: BarChart3 });
    }
    sections.push({
      id: 'attendance',
      label: 'Attendance & Time',
      icon: Clock,
      items: attendanceItems
    });

    const leaveItems = [
      { label: 'Leave Management', path: '/hr/leave-management', icon: Calendar },
    ];
    sections.push({
      id: 'leave',
      label: 'Leave',
      icon: Calendar,
      items: leaveItems
    });

    const payrollItems = [];
    if (canManagePayroll) {
      payrollItems.push({ label: 'Payroll Processing', path: '/hr/payroll-2026', icon: Calculator });
      payrollItems.push({ label: 'Payroll Approval', path: '/hr/payroll-approval', icon: Shield });
    }
    payrollItems.push({ label: 'My Payslips', path: '/hr/my-payslips', icon: FileText });
    sections.push({
      id: 'payroll',
      label: 'Payroll',
      icon: DollarSign,
      items: payrollItems
    });

    const perfItems = [
      { label: 'Goals & Reviews', path: '/hr/performance-management', icon: Target },
    ];
    if (canManagePerformance || isManager) {
      perfItems.push({ label: 'Appraisals', path: '/hr/performance', icon: Star });
    }
    sections.push({
      id: 'performance',
      label: 'Performance',
      icon: Target,
      items: perfItems
    });

    sections.push({ id: 'div-culture', label: 'Culture & Compliance', divider: true });

    sections.push({
      id: 'training',
      label: 'Training & Engagement',
      icon: GraduationCap,
      items: [
        { label: 'Learning (LMS)', path: '/hr/learning', icon: BookOpen },
        { label: 'Recognition', path: '/hr/engagement', icon: Heart },
      ]
    });

    const complianceItems = [];
    if (isHR) {
      complianceItems.push({ label: 'Discipline Cases', path: '/hr/discipline', icon: AlertTriangle });
    }
    complianceItems.push({ label: 'My Records', path: '/hr/discipline-grievance', icon: Scale });
    if (isHR) {
      complianceItems.push({ label: 'Compliance', path: '/hr/compliance', icon: Shield });
    }
    sections.push({
      id: 'discipline',
      label: 'Compliance & Discipline',
      icon: Gavel,
      items: complianceItems
    });

    const docItems = [];
    if (isHR) {
      docItems.push({ label: 'All Documents', path: '/hr/document-management', icon: FileText });
    }
    docItems.push({ label: 'My Documents', path: '/employee/my-documents', icon: FolderOpen });
    sections.push({
      id: 'documents',
      label: 'Documents',
      icon: FolderOpen,
      items: docItems
    });

    if (isHR) {
      sections.push({ id: 'div-analytics', label: 'Analytics & Admin', divider: true });

      sections.push({
        id: 'reports',
        label: 'Reports & Analytics',
        icon: BarChart3,
        items: [
          { label: 'Analytics Dashboard', path: '/hr/analytics', icon: BarChart3 },
          { label: 'Comprehensive Report', path: '/hr/comprehensive-report', icon: FileText },
          { label: 'AI Insights', path: '/hr/ai-insights', icon: Sparkles },
          { label: 'Audit Trail', path: '/hr/audit-trail', icon: ClipboardList },
        ]
      });

      sections.push({
        id: 'admin',
        label: 'HR Settings',
        icon: Settings,
        items: [
          { label: 'Configuration', path: '/hr/settings', icon: Settings },
          { label: 'Bulk Import/Export', path: '/hr/bulk-operations', icon: FileSpreadsheet },
          { label: 'System Admin', path: '/admin/super', icon: Shield },
        ]
      });
    }

    return sections;
  }, [isAdmin, isHR, isManager, isHRAdmin, canManageAttendance, canManageLeave, canManagePayroll, canManagePerformance, viewMode]);

  const sidebarContent = (isMobile = false) => (
    <>
      <div className="p-4 border-b border-white/10">
        <div className="flex items-center justify-between">
          {(sidebarOpen || isMobile) && (
            <Link to="/" className="flex items-center gap-2.5 text-white" data-testid="hr-logo">
              <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-lg flex items-center justify-center">
                <Users className="w-4 h-4" />
              </div>
              <div>
                <h1 className="font-bold text-sm leading-tight">Office Suite</h1>
                <p className="text-[10px] text-slate-400 leading-tight">Human Resources</p>
              </div>
            </Link>
          )}
          {!isMobile ? (
            <button 
              onClick={() => setSidebarOpen(!sidebarOpen)}
              data-testid="sidebar-toggle"
              className="p-1.5 hover:bg-white/10 rounded-lg text-slate-400 hover:text-white transition-colors"
            >
              {sidebarOpen ? <ChevronRight className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            </button>
          ) : (
            <button 
              onClick={handleMobileClose}
              className="p-1.5 hover:bg-white/10 rounded-lg text-white"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {isHRAdmin && (sidebarOpen || isMobile) && (
        <div className="px-3 py-2 border-b border-white/10">
          <button
            onClick={toggleViewMode}
            data-testid="view-mode-toggle"
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-all bg-white/5 hover:bg-white/10"
          >
            <div className={`p-1 rounded ${viewMode === 'admin' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-blue-500/20 text-blue-400'}`}>
              {viewMode === 'admin' ? <Shield className="w-3.5 h-3.5" /> : <UserCircle2 className="w-3.5 h-3.5" />}
            </div>
            <span className={`flex-1 text-left ${viewMode === 'admin' ? 'text-emerald-400' : 'text-blue-400'}`}>
              {viewMode === 'admin' ? 'Admin View' : 'My Profile'}
            </span>
            <ArrowLeftRight className="w-3.5 h-3.5 text-slate-500" />
          </button>
        </div>
      )}

      <nav className="flex-1 py-2 overflow-y-auto">
        {navSections.map(section => (
          <NavSection 
            key={section.id} 
            section={section}
            isActive={isActive}
            expandedSections={expandedSections}
            toggleSection={toggleSection}
            onMobileClose={handleMobileClose}
            sidebarOpen={isMobile ? true : sidebarOpen}
            mobileMenuOpen={mobileMenuOpen}
            pathname={location.pathname}
          />
        ))}
      </nav>

      <div className="p-3 border-t border-white/10">
        <Link
          to="/"
          onClick={isMobile ? handleMobileClose : undefined}
          data-testid="back-to-app"
          className="flex items-center gap-3 px-3 py-2 rounded-lg text-slate-400 hover:bg-white/10 hover:text-white transition-all text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          {(sidebarOpen || isMobile) && <span>Back to ERP</span>}
        </Link>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="hr-layout">
      <aside 
        className={`hidden lg:flex flex-col bg-[#0F172A] transition-all duration-300 sticky top-0 h-screen ${
          sidebarOpen ? 'w-60' : 'w-16'
        }`}
      >
        {sidebarContent(false)}
      </aside>

      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-[#0F172A] px-4 py-3 shadow-lg">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setMobileMenuOpen(true)}
            data-testid="mobile-menu-btn"
            className="p-2 text-white hover:bg-white/10 rounded-lg"
          >
            <Menu className="w-5 h-5" />
          </button>
          <h1 className="text-white font-bold text-sm">Office Suite HR</h1>
          <Link to="/" className="p-2 text-white hover:bg-white/10 rounded-lg">
            <ArrowLeft className="w-5 h-5" />
          </Link>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/60" onClick={handleMobileClose} />
          <aside className="absolute left-0 top-0 bottom-0 w-72 bg-[#0F172A] overflow-y-auto flex flex-col">
            {sidebarContent(true)}
          </aside>
        </div>
      )}

      <main className="flex-1 lg:p-6 p-4 pt-20 lg:pt-6 overflow-auto bg-slate-50">
        <div className="max-w-7xl mx-auto">
          {!hrAuthReady ? (
            <div className="flex items-center justify-center min-h-[60vh]">
              <div className="text-center">
                <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                <p className="text-slate-500 text-sm">Connecting to HR services...</p>
              </div>
            </div>
          ) : (
            <>
              {hrAuthError && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2 text-amber-800 text-sm">
                  <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                  <span>{hrAuthError}</span>
                </div>
              )}
              {children}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default HRLayout;
